import axios from "@common/api/desktop/axios";
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PROCESS_PERIOD_MODAL_TYPE } from "@reducers/ProcessPeriodModalReducer";
import ComplianceClient from "@common/api/ComplianceClient";
import ComplianceCMSClient from "@common/api/ComplianceCMSClient";
import ConfirmModalAction from "@common/redux/actions/ConfirmModalAction";
import AlertModalAction from "@common/redux/actions/AlertModalAction";
import { handleError } from "@common/components/helpers/error";
import useComplianceView from "@hooks/useComplianceView";
import useEventDetailsGrid from "@hooks/useEventDetailsGrid";
import { handleDownload } from "../helpers/downloads.js";
import useLogin from "@common/components/hooks/useLogin.js";
import { Menu, Tab } from "semantic-ui-react";
import CloseButton from "@buttons/CloseButton";
import availablePanes from "@helpers/panes";
import LoadingAction from "@common/redux/actions/LoadingAction";
import { fetchOperatorOptions } from "@actions";

export default function useProcessPeriodModal() {
  const data = useSelector((state) => state.processPeriodModal);
  const login = useSelector((state) => state.logIn);
  const [{ isDesktop }] = useLogin();

  const {
    filterSelected: { company, site, subcompany },
  } = useSelector((state) => state.complianceFilters);

  const { open } = data;
  const {
    operator,
    hasSelection,
    filters,
    filters: { status },
    quarter,
    quarter: { startDate, endDate, year, quarterName },
    setViewData,
  } = useComplianceView();

  const [complianceQuarterReportingOrgLevel, setComplianceQuarterReportingOrgLevel] =
    useState(null);
  const [processRequestFailed, setProcessRequestFailed] = useState(false);

  useEffect(() => {
    if (operator) {
      fetchOperatorOptions(operator)
        .then((options) => {
          setComplianceQuarterReportingOrgLevel(
            options?.complianceQuarterReportingOrgLevel || null,
          );
        })
        .catch((error) => {
          handleError(error);
          setComplianceQuarterReportingOrgLevel(null);
        });
    } else {
      setComplianceQuarterReportingOrgLevel(null);
    }
  }, [operator]);

  useEffect(() => {
    if (!operator || !hasSelection || !company?.id) {
      return;
    }

    const orgUnitId = complianceQuarterReportingOrgLevel === "SUB" 
      ? subcompany?.id 
      : company?.id;
    
    if (!orgUnitId) {
      return;
    }

    ComplianceClient.Process(orgUnitId)
      .then((quarter) => {
        setViewData({ quarter });
        setProcessRequestFailed(false);
      })
      .catch((error) => {
        setProcessRequestFailed(true);
        setViewData({
          quarter: {
            incompleteRecords: null,
            canBeProcessed: false,
            quarterName: "",
            startDate: null,
            endDate: null,
            year: null,
          },
        });
      });
  }, [company?.id, subcompany?.id, complianceQuarterReportingOrgLevel, operator, hasSelection]);

  const isLevelMismatched = useMemo(() => {
    if (!complianceQuarterReportingOrgLevel) {
      return site.id !== null || subcompany.id !== null || company.id === null;
    }

    switch (complianceQuarterReportingOrgLevel) {
      case "CO":
        return !(site.id === null && subcompany.id === null && company.id !== null);
      case "SUB":
        return !(site.id === null && subcompany.id !== null && company.id !== null);
      default:
        return site.id !== null || subcompany.id !== null || company.id === null;
    }
  }, [complianceQuarterReportingOrgLevel, site.id, subcompany.id, company.id]);

  const disableProcessQuarterBtn = useMemo(() => {
    if (processRequestFailed) {
      return true;
    }

    return isLevelMismatched;
  }, [processRequestFailed, isLevelMismatched]);

  const organisationUnitId = useMemo(() => {
    if (complianceQuarterReportingOrgLevel === "SUB") {
      return subcompany?.id;
    }
    return company?.id;
  }, [complianceQuarterReportingOrgLevel, subcompany?.id, company?.id]);

  const { onRefresh } = useEventDetailsGrid();

  const {
    filterSelected: {
      company: { id },
    },
  } = useSelector((state) => state.complianceFilters);

  const dispatch = useDispatch();

  const { sessionkey } = useSelector((state) => state.logIn ?? {});
  const { panes } = useSelector((state) => state.mainMenuState ?? {});

  const setProcessPeriodModal = (payload) =>
    dispatch({ type: PROCESS_PERIOD_MODAL_TYPE, payload });

  const setMenuState = (payload) => dispatch({ type: "MAIN_MENU", payload });

  const toggleProcessPeriodModal = () => setProcessPeriodModal({ open: !open });

  const menuItem = "GENERAL_REPORTS";

  const getExistingPanes = () =>
    availablePanes.map(({ key, title, component }) => {
      return {
        menuItem: (
          <Menu.Item key={key}>
            {title} <CloseButton onClose={(e) => closePane(key)} />
          </Menu.Item>
        ),
        pane: <Tab.Pane key={`tab_${key}`}>{component}</Tab.Pane>,
      };
    });

  const closePane = (key) => {
    const existingPanes = panes.filter((pane) => pane.menuItem.key !== key);

    let activeIndex = null;
    let section = "";

    if (existingPanes?.length) {
      activeIndex = existingPanes.length - 1;
      section = existingPanes[activeIndex].menuItem.key;
    }

    setMenuState({ panes: existingPanes, activeIndex, section });
  };

  const newPane = () => {
    const existingPanes = getExistingPanes();
    let newPane = existingPanes.find((pane) => pane.menuItem.key === menuItem);
    if (newPane) {
      const newPanes = [...panes, newPane];
      setMenuState({
        activeIndex: panes.length,
        panes: newPanes,
        section: menuItem,
      });
    }
  };

  const redirectToReports = () => {
    if (panes?.length === 0) {
      newPane();
      return;
    }

    let pane = panes?.findIndex((pane) => pane.menuItem.key === menuItem);
    if (pane > -1) {
      setMenuState({ activeIndex: pane, section: menuItem });
    } else {
      newPane();
    }
  };

  const onProcessClick = () => {
    if (isDesktop) sessionStorage.setItem("token", sessionkey);

    if (login?.name?.includes("(Super User)")) {
      AlertModalAction.open(
        `Cannot process return whilst logged in as a super user!`,
        "Error",
      );
      return;
    }

    if (isLevelMismatched) {
      AlertModalAction.open(
        `Process quarterly cannot process at this level!`,
        "Error",
      );
      return;
    }

    if (quarter?.canBeProcessed === false) {
      const reason = quarter?.canBeProcessedReason || "";
      const isIncompleteError = reason.toLowerCase().includes("incomplete");
      
      AlertModalAction.open(
        reason,
        "Error",
        () => {
          if (isIncompleteError) {
            const incompleteStatuses = [
              ...status.filter((x) => !x.isComplete).map((x) => x.id),
            ];
            setViewData({
              filters: {
                ...filters,
                startDate,
                endDate,
                selectedStatuses: incompleteStatuses,
              },
            });
          }
        },
      );
      return;
    }

    ComplianceCMSClient.SetUpReturn({
      tenantId: operator,
      year,
      quarter: quarter?.quarter,
      personId: 0,
      organisationUnitId: organisationUnitId,
    }).then((response) => {
      if (response?.errors?.length === 0) {
        ConfirmModalAction.open(
          `Once processed all records within this quarter will be set to "Processed" and cannot be edited.`,
          "Confirm you want to process this quarterly return",
          () => processReturns(),
        );
        return;
      }

      const incompleteError = response?.errors?.find((x) =>
        x.includes("incomplete"),
      );
      const processedError = response?.errors?.find((x) =>
        x.includes("processed"),
      );
      if (incompleteError) {
        ConfirmModalAction.open(
          `${quarterName} ${year} cannot be processed. ${incompleteError}. Would you like to view the incomplete records now?`,
          "There are outstanding pending or auth required events",
          async () => {
            const incompleteStatuses = [
              ...status.filter((x) => !x.isComplete).map((x) => x.id),
            ];
            setViewData({
              filters: {
                ...filters,
                startDate,
                endDate,
                selectedStatuses: incompleteStatuses,
              },
            });
          },
        );
      } else if (processedError) {
        ConfirmModalAction.open(
          "There are no outstanding quarterly returns. To view your historic returns download them via the reports tab.",
          "No quarterly returns to be processed",
          () => {
            redirectToReports();
          },
          () => {},
          { okLabel: "Reports" },
        );
      }
    });
  };

  const processReturns = () => {
    LoadingAction.set(true).then(() =>
      ComplianceCMSClient.CompleteReturn({
        tenantId: operator,
        year,
        quarter: quarter?.quarter,
        personId: 0,
        organisationUnitId: organisationUnitId,
      })
        .then(({ processedId }) => {
          ConfirmModalAction.open(
            "The quarterly return has now been processed. Download a copy to submit to the GC.",
            "Quarterly return complete",
            () => {
              downloadReport(processedId);
              onLoadProcess();
            },
            () => {
              onLoadProcess();
            },
            { okLabel: "Download" },
          );
        })
        .catch(handleError)
        .finally(() => LoadingAction.set(false)),
    );
  };

  const downloadReport = async (processedId) => {
    const response = await axios.get(`Compliance/Compliance/DownloadReturn/${processedId}`, {
      responseType: "arraybuffer",
    });
    handleDownload(response);
  };

  const onLoadProcess = () => {
    if (!operator || !hasSelection || !id) {
      return;
    }

    const orgUnitId = complianceQuarterReportingOrgLevel === "SUB" 
      ? subcompany?.id 
      : company?.id;
    
    if (!orgUnitId) {
      return;
    }
    
    ComplianceClient.Process(orgUnitId).then((quarter) => setViewData({ quarter }));
  };

  const onCompleteClick = () => {
    ComplianceCMSClient.CompleteReturn({
      tenantId: operator,
      year,
      quarter: quarter?.quarter,
      personId: 0,
      organisationUnitId: organisationUnitId,
    }).then(
      (response) => {
        handleDownload(response);
        AlertModalAction.open(
          `${quarterName} ${year} has been successfully processed!`,
          "Success",
          () => {
            setProcessPeriodModal({ open: false });
            onLoadProcess();
            onRefresh();
          },
        );
      },
      (error) => handleError(error),
    );
  };

  return {
    ...data,
    companyId: company.id,
    disableProcessQuarterBtn,
    isLevelMismatched,
    onLoadProcess,
    onProcessClick,
    onCompleteClick,
    toggleProcessPeriodModal,
    setProcessPeriodModal,
  };
}
